import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { AdminHeader, AdminTable } from './AdminShared';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ADMIN } from '@/constants/testIds';
import { Star } from 'lucide-react';

const EMPTY = {
  full_name: '', mobile: '', email: '', age: '', gender: 'male',
  address: '', city: '', lead_source: '', hearing_loss_classification: '',
  vip: false, notes: '', language: '', preferred_clinic_id: '',
};

export default function PatientsTab() {
  const [rows, setRows] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        api.get('/patients', { params: { q, limit: 500 } }),
        api.get('/clinics'),
      ]);
      setRows(p.data); setClinics(c.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [q]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...EMPTY, ...r, age: r.age || '' }); setOpen(true); };
  const save = async () => {
    try {
      const payload = { ...form, age: form.age ? parseInt(form.age) : null };
      if (editing) await api.patch(`/patients/${editing.id}`, payload);
      else await api.post('/patients', payload);
      toast.success('Saved'); setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save'); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.full_name}?`)) return;
    await api.delete(`/patients/${r.id}`); load(); toast.success('Deleted');
  };

  return (
    <div>
      <AdminHeader
        title="Patients"
        description="Lifelong patient records. Search by name or mobile."
        onCreate={openCreate}
        createLabel="New Patient"
        createTestId={ADMIN.createButton('patient')}
      />
      <div className="mb-3">
        <Input placeholder="Search by name or mobile…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 max-w-sm" data-testid="patient-search" />
      </div>
      <AdminTable
        loading={loading} rows={rows}
        columns={[
          { key: 'full_name', label: 'Name', render: (r) => (
            <span className="font-medium flex items-center gap-1">
              {r.full_name}
              {r.vip && <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />}
            </span>
          )},
          { key: 'mobile', label: 'Mobile' },
          { key: 'age', label: 'Age', render: (r) => r.age ? `${r.age}y` : '—' },
          { key: 'gender', label: 'Gender' },
          { key: 'lead_source', label: 'Lead source' },
          { key: 'hearing_loss', label: 'Loss', render: (r) => r.hearing_loss_classification || '—' },
          { key: 'journey', label: 'Journey', render: (r) => (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-stone-100 rounded">{r.journey_stage || 'new_lead'}</span>
          )},
        ]}
        rowTestId={ADMIN.row}
        onEdit={openEdit} onDelete={remove}
      />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Patient' : 'New Patient'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Full Name *</Label>
              <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="h-9" data-testid="patient-field-name" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Mobile *</Label>
                <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="h-9" data-testid="patient-field-mobile" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-9" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Age</Label>
                <Input type="number" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="h-9" />
              </div>
              <div>
                <Label>Gender</Label>
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Language</Label>
                <Input value={form.language || ''} onChange={(e) => setForm({ ...form, language: e.target.value })} className="h-9" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Lead Source</Label>
                <Input value={form.lead_source || ''} onChange={(e) => setForm({ ...form, lead_source: e.target.value })} className="h-9" />
              </div>
              <div>
                <Label>Hearing Loss</Label>
                <Select value={form.hearing_loss_classification || ''} onValueChange={(v) => setForm({ ...form, hearing_loss_classification: v })}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {['Mild','Moderate','Severe','Profound'].map((v) => (<SelectItem key={v} value={v}>{v}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Preferred Clinic</Label>
              <Select value={form.preferred_clinic_id || ''} onValueChange={(v) => setForm({ ...form, preferred_clinic_id: v })}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  {clinics.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Address</Label>
              <Textarea rows={2} value={form.address || ''} onChange={(e) => setForm({ ...form, address: e.target.value })} className="resize-none" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.vip} onCheckedChange={(v) => setForm({ ...form, vip: v })} />
              <Label>VIP patient</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid={ADMIN.saveButton}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
